import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Chip, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { getUnits, formatPace, formatElevation, formatDistance, formatVerticalSpeed } from '../../utils/units';

// Category → blue-to-violet gradient palette
const CATEGORY_COLORS = {
  'R-D4':  '#8ec5f5',  // sky blue
  'R-D3':  '#6898e8',  // cornflower blue
  'R-D2':  '#6b6ee0',  // blue-violet
  'R-D1':  '#8c48cc',  // indigo-violet
  'R-DHC': '#be35b8',  // vivid violet
};
const CATEGORY_TEXT = {
  'R-D4':  '#002a3a',
  'R-D3':  '#fff',
  'R-D2':  '#fff',
  'R-D1':  '#fff',
  'R-DHC': '#fff',
};

// Twistiness → traffic-light + intensity scale (dark variants — white text readable)
const TWISTINESS_COLORS = {
  'Straight':  '#27AE60',  // nephritis green  — go, bomb it
  'Flowy':     '#4D7C0F',  // dark lime        — fun, fast and active
  'Twisty':    '#9A7D0A',  // dark amber/gold  — caution, pay attention
  'Hairpins':  '#D35400',  // pumpkin orange   — slow down, hard braking
  'Technical': '#C0392B',  // pomegranate red  — stop, chaos, demanding
};

const TWISTINESS_TOOLTIP =
  'Twistiness (curvature index) measures how much this descent turns and zigzags.\n' +
  'Computed from GPS heading changes and straightness of each 50 m chunk.\n\n' +
  'Straight   — barely any turns, bomb straight down\n' +
  'Flowy      — smooth, rhythmic curves, fast and fun\n' +
  'Twisty     — frequent turns, requires line choice\n' +
  'Hairpins   — repeated tight turns, can\'t hold speed\n' +
  'Technical  — extreme switchbacks, very demanding terrain';

const formatGradient = (pct) => (pct != null ? `${pct.toFixed(1)}%` : '-');

const formatDuration = (secs) => {
  if (secs == null) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const RunningDescendsGrid = ({ descents = [], title = 'Detected Descents', onDescentClick, onDescentDoubleClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [units, setUnits] = useState(getUnits());
  const [zoomedRowId, setZoomedRowId] = useState(null);

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  if (!descents || descents.length === 0) {
    return (
      <Box sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: '8px', textAlign: 'center' }}>
        <Typography color={colors.grey[400]}>No descents detected in this activity.</Typography>
      </Box>
    );
  }

  const rows = descents.map((d, idx) => ({
    id: idx,
    category:            d.category,
    twistiness_label:    d.twistiness_label,
    avg_curvature_index: d.avg_curvature_index,
    start_km:            d.start_distance_m / 1000,
    end_km:              d.end_distance_m / 1000,
    length_km:           d.length_m / 1000,
    elevation_loss_m:    d.elevation_loss_m,
    duration_s:          d.duration_s,
    stopped_time_s:      d.stopped_time_s,
    avg_gradient_pct:    d.avg_gradient_pct,
    max_gradient_pct:    d.max_gradient_pct,
    avg_pace:            d.avg_pace,
    avg_gap:             d.avg_gap,
    avg_heart_rate:      d.avg_heart_rate,
    avg_vertical_speed:  d.moving_desc_vertical_speed || Math.abs(d.moving_vertical_speed || 0),
    moving_pace:         d.moving_pace,
    moving_gap:          d.moving_gap,
  }));

  const hasHR     = descents.some(d => d.avg_heart_rate != null);
  const hasMoving = descents.some(d => d.moving_pace != null);

  const columns = [
    {
      field: 'category',
      headerName: 'Cat.',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: CATEGORY_COLORS[params.value] || colors.blueAccent[500],
            color: CATEGORY_TEXT[params.value] || '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    {
      field: 'twistiness_label',
      headerName: 'Twistiness',
      width: 115,
      renderCell: (params) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{TWISTINESS_TOOLTIP}</span>} arrow>
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: TWISTINESS_COLORS[params.value] || colors.grey[600],
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '0.7rem',
              cursor: 'help',
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'avg_curvature_index',
      headerName: 'Curv.',
      width: 65,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'start_km',
      headerName: units === 'imperial' ? 'Start (mi)' : 'Start (km)',
      width: 100,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'end_km',
      headerName: units === 'imperial' ? 'End (mi)' : 'End (km)',
      width: 100,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'length_km',
      headerName: units === 'imperial' ? 'Length (mi)' : 'Length (km)',
      width: 110,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'elevation_loss_m',
      headerName: '↓ Loss',
      width: 90,
      valueFormatter: (value) => formatElevation(value, units),
    },
    {
      field: 'duration_s',
      headerName: 'Duration',
      width: 95,
      valueFormatter: (value) => formatDuration(value),
    },
    {
      field: 'stopped_time_s',
      headerName: 'Stopped',
      width: 90,
      valueFormatter: (value) => formatDuration(value),
    },
    {
      field: 'avg_vertical_speed',
      headerName: 'Moving Desc V.Spd',
      width: 105,
      valueFormatter: (value) => (value != null ? formatVerticalSpeed(Math.abs(value), units) : '-'),
    },
    {
      field: 'avg_gradient_pct',
      headerName: 'Avg %',
      width: 80,
      valueFormatter: (value) => formatGradient(value),
    },
    {
      field: 'max_gradient_pct',
      headerName: 'Max %',
      width: 80,
      valueFormatter: (value) => formatGradient(value),
    },
    {
      field: 'avg_pace',
      headerName: 'Avg Pace',
      width: 100,
      valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
    },
    {
      field: 'avg_gap',
      headerName: 'Avg Norm. Pace',
      width: 120,
      valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
    },
    ...(hasHR ? [{
      field: 'avg_heart_rate',
      headerName: 'Avg HR',
      width: 90,
      valueFormatter: (value) => (value != null ? `${Math.round(value)} bpm` : '-'),
    }] : []),
    ...(hasMoving ? [
      {
        field: 'moving_pace',
        headerName: 'Mov. Pace',
        width: 105,
        valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
      },
      {
        field: 'moving_gap',
        headerName: 'Mov. Norm. Pace',
        width: 105,
        valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
      },
    ] : []),
  ];

  const gridHeight = Math.min(108 + rows.length * 52, 600);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={2}>
        {title}
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        Click to highlight in chart. Double-click to zoom chart and map to that section.
      </Typography>
      <Box
        sx={{
          height: gridHeight,
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}`, color: colors.grey[100] },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[700], color: colors.grey[100], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.grey[800] },
          '& .MuiDataGrid-footerContainer': { backgroundColor: colors.grey[700], color: colors.grey[100], borderTop: 'none' },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': { color: colors.grey[100] },
          '& .MuiCheckbox-root': { color: `${colors.blueAccent[400]} !important` },
          '& .MuiDataGrid-row.row-zoomed': { backgroundColor: `${colors.grey[600]} !important` },
          '& .MuiDataGrid-row:hover': { backgroundColor: `${colors.grey[700]} !important` },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          getRowClassName={(params) => params.row.id === zoomedRowId ? 'row-zoomed' : ''}
          onRowClick={(params) => {
            const descent = descents[params.row.id];
            const payload = {
              leftKm:  descent.start_distance_m / 1000,
              rightKm: descent.end_distance_m / 1000,
              color:   TWISTINESS_COLORS[descent.twistiness_label] || '#6898e8',
            };
            if (onDescentClick) onDescentClick(payload);
          }}
          onRowDoubleClick={onDescentDoubleClick ? (params) => {
            const descent = descents[params.row.id];
            setZoomedRowId(params.row.id);
            onDescentDoubleClick({
              leftKm:  descent.start_distance_m / 1000,
              rightKm: descent.end_distance_m / 1000,
              color:   TWISTINESS_COLORS[descent.twistiness_label] || '#6898e8',
            });
          } : undefined}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};

export default RunningDescendsGrid;
